import { type StoreApi, useStore } from 'zustand';

import type { CommonStoreType } from './commonStore/types';

type WithSelectors<S> = S extends { getState: () => infer T }
  ? S & { use: { [K in keyof T]: () => T[K] } }
  : never;

export type SelectableStore<S extends StoreApi<unknown>> = S &
  (<T>(selector: (state: ReturnType<S['getState']>) => T) => T);

export type StoreSelectors<S extends StoreApi<object>> = SelectableStore<S> &
  WithSelectors<S>;

type ExtractState<S> = S extends { getState: () => infer T } ? T : never;

export const createSelectors = <S extends StoreApi<object>>(
  _store: S
): StoreSelectors<S> => {
  const useBoundStore = <T>(selector: (state: ExtractState<S>) => T): T =>
    useStore(_store, selector);

  Object.assign(useBoundStore, _store);

  useBoundStore.use = {} as {
    [K in keyof ExtractState<S>]: () => ExtractState<S>[K];
  };
  for (const k of Object.keys(_store.getState())) {
    useBoundStore.use[k as keyof ExtractState<S>] = () =>
      useBoundStore((s) => s[k as keyof ExtractState<S>]);
  }

  return useBoundStore as SelectableStore<S> & WithSelectors<S>;
};

export type CommonStoreSetState = StoreApi<CommonStoreType>['setState'];
